// Phase D — simplified client-portal Compliance Hub for a single project.
// Strictly portal-visible: progress + current stage, GT/BSC/Fire status,
// required documents, Safety Pack, open/overdue actions ("items requiring your
// action"), resident items and published reports.
import { useNavigate } from "react-router-dom";
import {
    Activity, Layers, Building2, Flame, FileText, PackageCheck, ShieldAlert,
    MessageSquare, ChevronRight, CheckCircle2, AlertTriangle, TrendingUp, FileCheck2,
    Plus, Siren,
} from "lucide-react";
import Pill from "@/components/Pill";

const toneFor = {
    green: { bg: "#E8F1E6", fg: "#3F7D3A" }, amber: { bg: "#FCF1DC", fg: "#C68A1C" },
    red: { bg: "#FBE8E5", fg: "#B33A2E" }, blue: { bg: "#E7EEF9", fg: "#2A5DB0" },
    neutral: { bg: "#F0EDE8", fg: "#6F6A64" },
};
const gtTone = (s) => ({ Complete: "green", "In progress": "blue", Gaps: "amber" }[s] || "neutral");
const bscTone = (s) => ({ Accepted: "green", "In review": "blue", Submitted: "blue", Draft: "amber", Rejected: "red" }[s] || "neutral");
const fireTone = (s) => ({ Satisfactory: "green", "Action required": "amber", Critical: "red" }[s] || "neutral");
const crossTone = (s) => (s === "Clear" ? "green" : s === "Incident recorded" ? "amber" : "neutral");
const sevTone = (s) => ({ Fatal: "red", Major: "red", Moderate: "amber", Minor: "neutral" }[s] || "neutral");

function StatusTile({ label, status, sub, tone, icon: Icon, testId }) {
    const t = toneFor[tone] || toneFor.neutral;
    return (
        <div data-testid={testId} className="rounded-[11px] border px-3.5 py-3" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: "#9A968F" }}>
                {Icon && <Icon className="w-3.5 h-3.5" />}{label}
            </div>
            <div className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold" style={{ background: t.bg, color: t.fg }}>{status}</div>
            {sub != null && <div className="text-[10.5px] mt-1" style={{ color: "#9A968F" }}>{sub}</div>}
        </div>
    );
}

function Section({ title, icon: Icon, count, children, testId }) {
    return (
        <div data-testid={testId} className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
            <h3 className="text-[13px] font-bold text-[#1F1F1F] flex items-center gap-2 mb-3">
                {Icon && <Icon className="w-4 h-4" style={{ color: "var(--theme-secondary, var(--brand-accent,#C75A1B))" }} />} {title}
                {count != null && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{count}</span>}
            </h3>
            {children}
        </div>
    );
}

function SafetyCrossSection({ incidents, stats, crossStatus, navigate, backParam }) {
    const now = new Date();
    const year = now.getFullYear(), month = now.getMonth();
    const today = new Date(year, month, now.getDate());
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDow = new Date(year, month, 1).getDay();
    const dayMap = {};
    incidents.forEach((i) => {
        if (!i.occurred_at) return;
        const d = new Date(i.occurred_at);
        if (d.getFullYear() !== year || d.getMonth() !== month) return;
        const day = d.getDate();
        const severe = i.riddor_reportable || ["Major", "Fatal"].includes(i.severity);
        if (severe) dayMap[day] = "red";
        else if (dayMap[day] !== "red") dayMap[day] = "amber";
    });
    const cells = [];
    for (let b = 0; b < firstDow; b++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const colorFor = (d) => {
        const dt = new Date(year, month, d);
        if (dayMap[d] === "red") return { bg: "#B33A2E", fg: "#fff" };
        if (dayMap[d] === "amber") return { bg: "#E8A93C", fg: "#fff" };
        if (dt <= today) return { bg: "#DCEAD7", fg: "#3F7D3A" };
        return { bg: "#F6F4F0", fg: "#C7C0B6" };
    };
    const monthLabel = now.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const recent = [...incidents].sort((a, b) => (b.occurred_at || "").localeCompare(a.occurred_at || ""));

    return (
        <div data-testid="portal-hub-safety-cross" className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="text-[13px] font-bold text-[#1F1F1F] flex items-center gap-2">
                    <Siren className="w-4 h-4" style={{ color: crossStatus === "Clear" ? "#3F7D3A" : "#C68A1C" }} /> Safety Cross &amp; incidents
                </h3>
                <div className="flex items-center gap-2 text-[11px]" style={{ color: "#6F6A64" }}>
                    <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: "#F0EDE8" }}>{stats.total} logged</span>
                    <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: stats.open > 0 ? "#FCF1DC" : "#E8F1E6", color: stats.open > 0 ? "#C68A1C" : "#3F7D3A" }}>{stats.open} open</span>
                    {stats.riddor > 0 && <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: "#FBE8E5", color: "#B33A2E" }}>{stats.riddor} RIDDOR</span>}
                </div>
            </div>
            <div className="grid lg:grid-cols-[260px_1fr] gap-5">
                <div data-testid="portal-safety-cross-calendar">
                    <div className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: "#9A968F" }}>{monthLabel}</div>
                    <div className="grid grid-cols-7 gap-1">
                        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                            <div key={i} className="text-[9px] font-bold text-center" style={{ color: "#C7C0B6" }}>{d}</div>
                        ))}
                        {cells.map((d, i) => {
                            if (d === null) return <div key={i} />;
                            const c = colorFor(d);
                            return (
                                <div key={i} className="aspect-square rounded-[5px] grid place-items-center text-[10px] font-semibold" style={{ background: c.bg, color: c.fg }}>{d}</div>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-[10px] flex-wrap" style={{ color: "#6F6A64" }}>
                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-[3px]" style={{ background: "#DCEAD7" }} /> Incident-free</span>
                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-[3px]" style={{ background: "#E8A93C" }} /> Minor</span>
                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-[3px]" style={{ background: "#B33A2E" }} /> Major / RIDDOR</span>
                    </div>
                </div>
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: "#9A968F" }}>Accidents &amp; incidents</div>
                    {recent.length === 0 ? (
                        <div className="text-[12px] italic py-3 flex items-center gap-2" style={{ color: "#3F7D3A" }}><CheckCircle2 className="w-4 h-4" /> No accidents or incidents logged on this project.</div>
                    ) : (
                        <div className="space-y-1.5">
                            {recent.map((i) => (
                                <button key={i.id} data-testid={`portal-incident-${i.id}`} onClick={() => navigate(`/app/incidents${backParam ? "?" + backParam.slice(1) : ""}`)}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-[9px] border text-left hover:bg-[#F6F4F0] transition-colors" style={{ borderColor: "#EFEAE3" }}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate flex items-center gap-1.5">
                                            {i.incident_type} <span className="font-mono text-[10px]" style={{ color: "#9A968F" }}>{i.reference}</span>
                                            {i.riddor_reportable && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FBE8E5", color: "#B33A2E" }}>RIDDOR</span>}
                                        </div>
                                        <div className="text-[10.5px] truncate" style={{ color: "#9A968F" }}>
                                            {(i.occurred_at || "").slice(0, 10)}{i.location ? ` · ${i.location}` : ""}
                                        </div>
                                    </div>
                                    <Pill tone={sevTone(i.severity)}>{i.severity}</Pill>
                                    <Pill tone={i.status === "Closed" ? "green" : "amber"}>{i.status}</Pill>
                                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "#C7C0B6" }} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


export default function PortalComplianceHub({ compliance, project, backParam = "" }) {
    const navigate = useNavigate();
    if (!compliance) {
        return <div data-testid="portal-hub-loading" className="text-[12px] italic p-6 text-center" style={{ color: "#9A968F" }}>Loading…</div>;
    }
    const s = compliance.summary;
    const pv = compliance.portal_view || {};
    const cdoc = s.critical_documents || { total: 0, approved: 0, outstanding: 0, pct: 100, ready: true };
    const packs = compliance.doc_packs || [];
    const reqDocs = packs.filter((d) => !d.safety_pack);
    const safetyPack = pv.safety_pack || [];
    const reports = pv.published_reports || [];
    const actionItems = pv.client_action_items || [];
    const residentItems = pv.resident_items || [];
    const incidents = pv.incidents || [];
    const incStats = pv.incident_stats || { total: 0, open: 0, riddor: 0 };
    const safetyCross = pv.safety_cross || s.safety_cross || { status: "Clear", open_incidents: 0, incidents_recent: 0 };
    const overdueCount = actionItems.filter((a) => a.overdue).length;
    const stageLabel = (project.stages || []).find((st) => st.key === (pv.workflow_stage || project.workflow_stage))?.label || "—";
    const accent = "var(--theme-secondary, var(--brand-accent,#C75A1B))";

    const DocRow = ({ d }) => {
        const approved = d.status === "Approved";
        return (
            <button data-testid={`portal-doc-${d.id}`} onClick={() => navigate(`/app/documents?open=${d.id}${backParam}`)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-[9px] border text-left hover:bg-[#F6F4F0] transition-colors" style={{ borderColor: "#EFEAE3" }}>
                <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate flex items-center gap-1.5">
                        {d.critical && <span title="Critical" style={{ color: "#C68A1C" }}>★</span>}{d.title || d.filename}
                    </div>
                    {d.phase_label && <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{d.phase_label}</div>}
                </div>
                <Pill tone={approved ? "green" : (d.status === "Required" ? "amber" : "neutral")}>{d.status}</Pill>
                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "#C7C0B6" }} />
            </button>
        );
    };

    return (
        <div data-testid="portal-hub" className="space-y-4">
            {/* Progress + current stage */}
            <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="text-[13px] font-bold text-[#1F1F1F] flex items-center gap-2"><Activity className="w-4 h-4" style={{ color: accent }} /> Compliance summary</h3>
                    <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "#6F6A64" }}>
                        <TrendingUp className="w-3.5 h-3.5" style={{ color: accent }} />
                        Progress — <span className="font-mono text-[14px] font-bold text-[#1F1F1F]" data-testid="portal-hub-progress">{pv.progress_pct ?? project.progress_pct ?? 0}%</span>
                    </div>
                </div>
                <div className="text-[11.5px] mt-1" style={{ color: "#9A968F" }}>Current stage: <span className="font-semibold text-[#6F6A64]" data-testid="portal-hub-stage">{stageLabel}</span></div>
                <div className="h-2.5 rounded-full overflow-hidden mt-3" style={{ background: "#EFEAE3" }}>
                    <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pv.progress_pct ?? project.progress_pct ?? 0}%`, background: accent }} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                    <StatusTile label="Golden Thread" status={`${s.golden_thread.completeness_pct}%`} sub={s.golden_thread.status} tone={gtTone(s.golden_thread.status)} icon={Layers} testId="portal-hub-status-gt" />
                    <StatusTile label="Building Safety Case" status={s.bsc.status} tone={bscTone(s.bsc.status)} icon={Building2} testId="portal-hub-status-bsc" />
                    <StatusTile label="Fire Safety" status={s.fire_safety.status} tone={fireTone(s.fire_safety.status)} icon={Flame} testId="portal-hub-status-fire" />
                    <StatusTile label="Open actions" status={`${s.open_compliance_actions}`} sub={overdueCount > 0 ? `${overdueCount} overdue` : "none overdue"} tone={overdueCount > 0 ? "red" : "neutral"} icon={CheckCircle2} testId="portal-hub-status-actions" />
                    <StatusTile label="Critical docs" status={`${cdoc.approved}/${cdoc.total}`} sub={cdoc.ready ? "all approved" : `${cdoc.outstanding} outstanding`} tone={cdoc.ready ? "green" : (cdoc.total ? "amber" : "neutral")} icon={PackageCheck} testId="portal-hub-status-docs" />
                    <StatusTile label="Resident items" status={`${s.resident_engagement.open}`} sub={s.resident_engagement.status} tone={s.resident_engagement.open > 0 ? "amber" : "neutral"} icon={MessageSquare} testId="portal-hub-status-residents" />
                    <StatusTile label="Safety Cross" status={safetyCross.status} sub={`${safetyCross.open_incidents} open · ${incStats.total} logged`} tone={crossTone(safetyCross.status)} icon={Plus} testId="portal-hub-status-safety-cross" />
                </div>
            </div>

            {/* Items requiring your action */}
            <div data-testid="portal-hub-action-items" className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: actionItems.length ? "#F0D9B5" : "#E5E0DA", background: actionItems.length ? "#FFFCF6" : "#fff" }}>
                <h3 className="text-[13px] font-bold text-[#1F1F1F] flex items-center gap-2 mb-3">
                    <ShieldAlert className="w-4 h-4" style={{ color: overdueCount ? "#B33A2E" : "#C68A1C" }} /> Items requiring your action
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{actionItems.length}</span>
                </h3>
                {actionItems.length === 0 ? (
                    <div className="text-[12px] italic py-3 flex items-center gap-2" style={{ color: "#3F7D3A" }}><CheckCircle2 className="w-4 h-4" /> Nothing outstanding — you're all caught up.</div>
                ) : (
                    <div className="space-y-1.5">
                        {actionItems.map((a) => (
                            <button key={a.id} data-testid={`portal-action-${a.id}`} onClick={() => navigate(`/app/actions?open=${a.id}${backParam}`)}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-[9px] border text-left hover:bg-[#F6F4F0] transition-colors" style={{ borderColor: a.overdue ? "#F0C9B7" : "#EFEAE3" }}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{a.title}</div>
                                    <div className="text-[10.5px] flex items-center gap-2" style={{ color: a.overdue ? "#B33A2E" : "#9A968F" }}>
                                        {a.overdue && <AlertTriangle className="w-3 h-3" />}
                                        {a.due_date ? (a.overdue ? `Overdue · due ${a.due_date}` : `Due ${a.due_date}`) : "No due date"}
                                        {a.phase_label ? ` · ${a.phase_label}` : ""}
                                    </div>
                                </div>
                                <Pill tone={a.priority === "Critical" || a.priority === "High" ? "red" : a.priority === "Medium" ? "amber" : "neutral"}>{a.priority}</Pill>
                                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "#C7C0B6" }} />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Safety Cross + Accidents & incidents */}
            <SafetyCrossSection incidents={incidents} stats={incStats} crossStatus={safetyCross.status} navigate={navigate} backParam={backParam} />

            <div className="grid lg:grid-cols-2 gap-4">
                {/* Required documents */}
                <Section title="Required documents" icon={PackageCheck} count={reqDocs.length} testId="portal-hub-reqdocs">
                    {reqDocs.length === 0 ? <div className="text-[12px] italic py-2" style={{ color: "#9A968F" }}>No required documents shared yet.</div>
                        : <div className="space-y-1.5">{reqDocs.map((d) => <DocRow key={d.id} d={d} />)}</div>}
                </Section>

                {/* Safety Pack */}
                <Section title="Safety / Resident Information pack" icon={ShieldAlert} count={safetyPack.length} testId="portal-hub-safety-pack">
                    {safetyPack.length === 0 ? <div className="text-[12px] italic py-2" style={{ color: "#9A968F" }}>No safety pack items shared yet.</div>
                        : <div className="space-y-1.5">{safetyPack.map((d) => <DocRow key={d.id} d={d} />)}</div>}
                </Section>

                {/* Published reports */}
                <Section title="Published reports" icon={FileCheck2} count={reports.length} testId="portal-hub-reports">
                    {reports.length === 0 ? <div className="text-[12px] italic py-2" style={{ color: "#9A968F" }}>No reports published yet.</div>
                        : <div className="space-y-1.5">{reports.map((d) => <DocRow key={d.id} d={d} />)}</div>}
                </Section>

                {/* Resident items */}
                <Section title="Resident engagement" icon={MessageSquare} count={residentItems.length} testId="portal-hub-resident-items">
                    {residentItems.length === 0 ? <div className="text-[12px] italic py-2" style={{ color: "#9A968F" }}>No open resident concerns.</div>
                        : <div className="space-y-1.5">{residentItems.map((c) => (
                            <button key={c.id} data-testid={`portal-resident-${c.id}`} onClick={() => navigate(`/app/residents?open=${c.id}${backParam}`)}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-[9px] border text-left hover:bg-[#F6F4F0] transition-colors" style={{ borderColor: "#EFEAE3" }}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{c.concern_type} — {c.reference}</div>
                                    <div className="text-[10.5px] truncate" style={{ color: "#9A968F" }}>{c.description}</div>
                                </div>
                                <Pill tone={c.status === "Open" ? "amber" : "blue"}>{c.status}</Pill>
                                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "#C7C0B6" }} />
                            </button>
                        ))}</div>}
                </Section>
            </div>
        </div>
    );
}
